/**
 * Reading direction, observed from the document.
 *
 * CLAUDE.md §6 requires a real RTL switch for Dhivehi, and §11 requires it to be
 * built from logical properties rather than a mirrored stylesheet. Almost all of
 * that needs no JavaScript at all: `dir="rtl"` on `<html>` plus
 * `margin-inline-start`, `inset-inline-end`, `text-align: start` and Tailwind's
 * `ms-`/`me-`/`ps-`/`pe-`/`start-`/`end-` utilities flip on their own.
 *
 * Three things cannot: a `transform: translateX()` inside a keyframe, a swipe
 * gesture direction, and the glyph inside a directional icon. Those read the
 * direction from here.
 *
 * The source of truth is the `dir` attribute the app sets on `<html>`, not the
 * locale — a component library has no business knowing which locales are RTL,
 * and reading the DOM stays correct inside a `dir="rtl"` subtree too.
 */

import { computed, readonly, ref, type ComputedRef, type Ref } from 'vue'

export type Direction = 'ltr' | 'rtl'

const direction = ref<Direction>('ltr')

let observer: MutationObserver | null = null

function readFromDocument(): Direction {
  if (typeof document === 'undefined') return 'ltr'
  return document.documentElement.getAttribute('dir') === 'rtl' ? 'rtl' : 'ltr'
}

/**
 * One observer for the whole application. Started lazily by the first consumer
 * and deliberately never stopped: the state outlives any single component, and
 * a component that unmounts must not blind the ones still mounted.
 */
function observe(): void {
  if (observer !== null || typeof document === 'undefined') return
  direction.value = readFromDocument()
  if (typeof MutationObserver === 'undefined') return
  observer = new MutationObserver(() => {
    direction.value = readFromDocument()
  })
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['dir'] })
}

/** The document's reading direction, kept in sync with `<html dir>`. */
export function useDirection(): Readonly<Ref<Direction>> {
  observe()
  return readonly(direction)
}

/** True while the document reads right-to-left. */
export function useIsRtl(): ComputedRef<boolean> {
  observe()
  return computed(() => direction.value === 'rtl')
}

/**
 * Set the document direction. Apps call this from their i18n module when the
 * locale changes; it lives here so the library and the app never disagree about
 * which attribute carries the direction.
 */
export function applyDirection(value: Direction): void {
  direction.value = value
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('dir', value)
}

/** Test seam: drop the observer and reset to left-to-right. */
export function resetDirection(): void {
  observer?.disconnect()
  observer = null
  direction.value = 'ltr'
}
